import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ReactSelect from 'react-select';
import api from '../../api';
import { AuthContext } from '../../components/AuthContext';
import Swal from 'sweetalert2';
import {
  PlusIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  CheckIcon,
  XMarkIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  DocumentDuplicateIcon,
  CurrencyRupeeIcon,
  CalendarIcon,
  BuildingStorefrontIcon,
  ExclamationTriangleIcon,
  ShoppingCartIcon
} from '@heroicons/react/24/outline';

const PurchaseIndent = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Form state
  const [form, setForm] = useState({
    brand: null,
    dateRange: {
      startDate: new Date(),
      endDate: new Date()
    },
    mandatoryItems: [],
    additionalItems: [],
    remarks: '',
    estimatedCost: 0
  });

  // Data states
  const [brands, setBrands] = useState([]);
  const [purchaseIndents, setPurchaseIndents] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  
  // UI states
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModal, setViewModal] = useState({ open: false, purchaseIndent: null });
  const [step, setStep] = useState(1); // 1: Brand & Date, 2: Mandatory Items, 3: Additional Items, 4: Review
  const [stockValidationModal, setStockValidationModal] = useState({ open: false, items: [] });
  const [itemSelectionModal, setItemSelectionModal] = useState({ open: false, selectedItems: [] });
  const [itemSearch, setItemSearch] = useState(''); // Search state for additional items
  const [itemSearchLoading, setItemSearchLoading] = useState(false);
  const [filteredItemsCount, setFilteredItemsCount] = useState(0); // Track how many items were filtered out

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    status: '',
    brandId: ''
  });

  // Status options
  const statusOptions = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'APPROVED', label: 'Approved' },
    { value: 'REJECTED', label: 'Rejected' }
  ];

  // Refs for keyboard navigation
  const formRef = useRef(null);
  const brandRef = useRef(null);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  // Alert functions
  const showSuccessAlert = (title, message) => {
    Swal.fire({
      icon: 'success',
      title,
      text: message,
      confirmButtonColor: '#10B981',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
      background: '#F0FDF4',
      color: '#065F46'
    });
  };

  const showErrorAlert = (title, message) => {
    Swal.fire({
      icon: 'error',
      title,
      text: message,
      confirmButtonColor: '#EF4444',
      background: '#FEF2F2',
      color: '#991B1B'
    });
  };

  const showWarningAlert = (title, message) => {
    return Swal.fire({
      icon: 'warning',
      title,
      text: message,
      showCancelButton: true,
      confirmButtonColor: '#F59E0B',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, proceed!',
      cancelButtonText: 'Cancel',
      background: '#FFFBEB',
      color: '#92400E'
    });
  };

  useEffect(() => {
    fetchPurchaseIndents();
    fetchBrands();
  }, [page, filters]);

  // Debounced search for additional items
  useEffect(() => {
    if (step === 3 && form.brand) {
      const debounceTimer = setTimeout(() => {
        fetchAvailableItems(itemSearch);
      }, 300);

      return () => clearTimeout(debounceTimer);
    }
  }, [itemSearch, step, form.brand]);

  const fetchPurchaseIndents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/purchase-indents', {
        params: {
          page,
          limit: 10,
          userId: user.user._id,
          ...filters
        }
      });
      setPurchaseIndents(response.data.purchaseIndents);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error fetching purchase indents:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await api.get('/purchase-indents/brands', {
        params: {
          userId: user.user._id
        }
      });
      setBrands(response.data.map(brand => ({
        value: brand._id,
        label: brand.name
      })));
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchMandatoryItems = async () => {
    try {
      if (!form.brand || !form.dateRange.startDate || !form.dateRange.endDate) {
        return;
      }

      const response = await api.get('/purchase-indents/mandatory-items', {
        params: {
          brandId: form.brand.value,
          startDate: form.dateRange.startDate.toISOString(),
          endDate: form.dateRange.endDate.toISOString(),
          userId: user.user._id
        }
      });

      setForm(prev => ({
        ...prev,
        mandatoryItems: response.data
      }));
    } catch (error) {
      console.error('Error fetching mandatory items:', error);
      
      if (error.response && error.response.data && error.response.data.errorType === 'DATE_RANGE_OVERLAP') {
        const overlappingIndents = error.response.data.overlappingIndents;
        let errorMessage = `Date range overlaps with existing purchase indents for ${form.brand.label}:\n\n`;
        
        overlappingIndents.forEach((indent, index) => {
          errorMessage += `${index + 1}. ${indent.indentNumber} (${new Date(indent.dateRange.startDate).toLocaleString()} - ${new Date(indent.dateRange.endDate).toLocaleString()})\n`;
        });
        
        errorMessage += '\nPlease select a different date range that does not overlap with existing purchase indents.';
        
        await showErrorAlert('Date Range Conflict', errorMessage);
      } else {
        await showErrorAlert('Error', error.response?.data?.message || 'Failed to fetch mandatory items. Please try again.');
      }
    }
  };

  const fetchAvailableItems = async (search = '') => {
    try {
      if (!form.brand) return;

      setItemSearchLoading(true);
      const response = await api.get('/purchase-indents/items', {
        params: {
          brandId: form.brand.value,
          search,
          page: 1,
          limit: 50,
          userId: user.user._id
        }
      });

      // Filter out items that are already in mandatory items or additional items
      const mandatoryItemIds = form.mandatoryItems.map(item => {
        // Handle both string and ObjectId formats
        return typeof item.item === 'object' ? item.item._id || item.item : item.item;
      });
      const additionalItemIds = form.additionalItems.map(item => {
        // Handle both string and ObjectId formats
        return typeof item.item === 'object' ? item.item._id || item.item : item.item;
      });
      const existingItemIds = [...mandatoryItemIds, ...additionalItemIds];
      
      const originalCount = response.data.length;
      const filteredItems = response.data.filter(item => 
        !existingItemIds.includes(item._id) && !existingItemIds.includes(item._id.toString())
      );
      
      const filteredCount = originalCount - filteredItems.length;
      
      // Log filtering info for debugging
      if (filteredCount > 0) {
        console.log(`Filtered out ${filteredCount} items that are already selected`);
      }
      
      setFilteredItemsCount(filteredCount);
      setAvailableItems(filteredItems);
    } catch (error) {
      console.error('Error fetching available items:', error);
      setAvailableItems([]);
    } finally {
      setItemSearchLoading(false);
    }
  };

  const handleBrandDateNext = async () => {
    if (!form.brand || !form.dateRange.startDate || !form.dateRange.endDate) {
      await showErrorAlert('Validation Error', 'Please select brand and date range');
      return;
    }

    if (form.dateRange.endDate <= form.dateRange.startDate) {
      await showErrorAlert('Invalid Date Range', 'End date must be after start date');
      return;
    }

    setLoading(true);
    try {
      await fetchMandatoryItems();
      // Only proceed to next step if no error occurred (fetchMandatoryItems would have shown error)
      if (form.mandatoryItems || form.mandatoryItems?.length >= 0) {
        setStep(2);
      }
    } catch (error) {
      // Error already handled in fetchMandatoryItems
      console.error('Error in handleBrandDateNext:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMandatoryItemsNext = () => {
    setStep(3);
    setItemSearch(''); // Clear previous search
    fetchAvailableItems(); // Load initial items
  };

  const handleMandatoryItemChange = (index, field, value) => {
    const updatedItems = [...form.mandatoryItems];
    updatedItems[index][field] = value;

    // Recalculate required quantity for boxes-pieces items
    if (field === 'boxes' || field === 'pieces') {
      if (updatedItems[index].quantityType === 'boxes-pieces') {
        const pcsPerBox = updatedItems[index].pcsPerBox || 1;
        updatedItems[index].requiredQuantity = 
          (updatedItems[index].boxes || 0) * pcsPerBox + 
          (updatedItems[index].pieces || 0);
      }
    } else if (field === 'quantity') {
      updatedItems[index].requiredQuantity = value;
    }

    setForm(prev => ({
      ...prev,
      mandatoryItems: updatedItems
    }));
  };

  const handleAddAdditionalItem = (item) => {
    const additionalItem = {
      itemType: item.itemType,
      item: item._id,
      itemName: item.itemName,
      brandName: item.brand ? item.brand.name : 'N/A',
      quantityType: item.calculationType === 'pcsPerBox' ? 'boxes-pieces' : 'quantity',
      quantity: 1,
      boxes: 0,
      pieces: 0,
      unit: item.unit || 'pcs',
      pcsPerBox: item.pcsPerBox || 1,
      currentStock: parseFloat(item.closingBalance) || 0,
      reservedStock: 0,
      requiredQuantity: 1,
      isStockValidated: false,
      stockValidationMessage: ''
    };

    setForm(prev => ({
      ...prev,
      additionalItems: [...prev.additionalItems, additionalItem]
    }));
    
    // Refresh available items to remove the added item from the list
    fetchAvailableItems(itemSearch);
  };

  const handleAdditionalItemChange = (index, field, value) => {
    const updatedItems = [...form.additionalItems];
    updatedItems[index][field] = value;

    // Recalculate required quantity for boxes-pieces items
    if (field === 'boxes' || field === 'pieces') {
      if (updatedItems[index].quantityType === 'boxes-pieces') {
        const pcsPerBox = updatedItems[index].pcsPerBox || 1;
        updatedItems[index].requiredQuantity = 
          (updatedItems[index].boxes || 0) * pcsPerBox + 
          (updatedItems[index].pieces || 0);
      }
    } else if (field === 'quantity') {
      updatedItems[index].requiredQuantity = value;
    }

    setForm(prev => ({
      ...prev,
      additionalItems: updatedItems
    }));
  };

  const handleRemoveAdditionalItem = (index) => {
    const updatedItems = [...form.additionalItems];
    updatedItems.splice(index, 1);
    setForm(prev => ({
      ...prev,
      additionalItems: updatedItems
    }));
    
    // Refresh available items to include the removed item back in the list
    fetchAvailableItems(itemSearch);
  };

  // Helper function to check if an item is already selected
  const isItemAlreadySelected = (itemId) => {
    const mandatoryItemIds = form.mandatoryItems.map(item => {
      return typeof item.item === 'object' ? item.item._id || item.item : item.item;
    });
    const additionalItemIds = form.additionalItems.map(item => {
      return typeof item.item === 'object' ? item.item._id || item.item : item.item;
    });
    const allSelectedIds = [...mandatoryItemIds, ...additionalItemIds];
    return allSelectedIds.includes(itemId) || allSelectedIds.includes(itemId.toString());
  };

  const validateStock = async () => {
    try {
      const allItems = [...form.mandatoryItems, ...form.additionalItems];
      
      const response = await api.post('/purchase-indents/validate-stock', {
        items: allItems
      }, {
        params: {
          userId: user.user._id
        }
      });

      const validatedItems = response.data;
      
      // Update mandatory items
      const mandatoryValidated = validatedItems.slice(0, form.mandatoryItems.length);
      const additionalValidated = validatedItems.slice(form.mandatoryItems.length);

      setForm(prev => ({
        ...prev,
        mandatoryItems: mandatoryValidated,
        additionalItems: additionalValidated
      }));

      setStockValidationModal({ open: true, items: validatedItems });
    } catch (error) {
      console.error('Error validating stock:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const purchaseIndentData = {
        brand: form.brand.value,
        dateRange: {
          startDate: form.dateRange.startDate,
          endDate: form.dateRange.endDate
        },
        mandatoryItems: form.mandatoryItems,
        additionalItems: form.additionalItems,
        remarks: form.remarks,
        estimatedCost: form.estimatedCost
      };

      const response = await api.post('/purchase-indents', purchaseIndentData, {
        params: {
          userId: user.user._id
        }
      });

      await showSuccessAlert('Success!', 'Purchase indent created successfully! ✅');
      resetForm();
      setTabValue(0);
      fetchPurchaseIndents();
    } catch (error) {
      console.error('Error saving purchase indent:', error);
      
      if (error.response && error.response.data && error.response.data.errorType === 'DATE_RANGE_OVERLAP') {
        const overlappingIndents = error.response.data.overlappingIndents;
        let errorMessage = `Date range overlaps with existing purchase indents for ${form.brand.label}:\n\n`;
        
        overlappingIndents.forEach((indent, index) => {
          errorMessage += `${index + 1}. ${indent.indentNumber} (${new Date(indent.dateRange.startDate).toLocaleString()} - ${new Date(indent.dateRange.endDate).toLocaleString()})\n`;
        });
        
        errorMessage += '\nPlease go back and select a different date range that does not overlap with existing purchase indents.';
        
        await showErrorAlert('Date Range Conflict', errorMessage);
      } else {
        await showErrorAlert('Error', error.response?.data?.message || 'Error saving purchase indent. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };



  const resetForm = () => {
    setForm({
      brand: null,
      dateRange: {
        startDate: new Date(),
        endDate: new Date()
      },
      mandatoryItems: [],
      additionalItems: [],
      remarks: '',
      estimatedCost: 0
    });
    setStep(1);
    setModalVisible(false);
    setItemSearch(''); // Clear search
    setAvailableItems([]); // Clear available items
    setFilteredItemsCount(0); // Clear filtered count
  };

  const handleView = (purchaseIndent) => {
    setViewModal({ open: true, purchaseIndent });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED': return 'bg-green-100 text-green-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Step 1: Brand & Date Selection</h3>
              <p className="text-blue-700 text-sm">Select the brand and date range to fetch mandatory items from pending proforma invoices.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center">
                    <BuildingStorefrontIcon className="h-4 w-4 mr-2" />
                    Brand *
                  </div>
                </label>
                <ReactSelect
                  ref={brandRef}
                  value={form.brand}
                  options={brands}
                  onChange={(selectedOption) => setForm({ ...form, brand: selectedOption })}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Select brand..."
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: '44px',
                      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                      borderRadius: '8px',
                      '&:hover': {
                        borderColor: '#3b82f6'
                      }
                    })
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    Date Range *
                  </div>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <DatePicker
                    ref={startDateRef}
                    selected={form.dateRange.startDate}
                    onChange={(date) => setForm({ 
                      ...form, 
                      dateRange: { ...form.dateRange, startDate: date } 
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholderText="Start Date & Time"
                    dateFormat="dd/MM/yyyy HH:mm"
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    timeCaption="Time"
                  />
                  <DatePicker
                    ref={endDateRef}
                    selected={form.dateRange.endDate}
                    onChange={(date) => setForm({ 
                      ...form, 
                      dateRange: { ...form.dateRange, endDate: date } 
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholderText="End Date & Time"
                    dateFormat="dd/MM/yyyy HH:mm"
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    timeCaption="Time"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleBrandDateNext}
                disabled={!form.brand || !form.dateRange.startDate || !form.dateRange.endDate || loading}
                className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Loading...' : 'Next: Fetch Mandatory Items'}
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-green-900 mb-2">Step 2: Mandatory Items</h3>
              <p className="text-green-700 text-sm">
                Items marked as "TO BE ORDERED" from pending proforma invoices for <strong>{form.brand?.label}</strong> between {form.dateRange.startDate?.toLocaleString()} and {form.dateRange.endDate?.toLocaleString()}
              </p>
            </div>

            {form.mandatoryItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <ExclamationTriangleIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>No mandatory items found for the selected brand and date range.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {form.mandatoryItems.map((item, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 break-words leading-relaxed">
                          {item.itemName}
                        </h4>
                        <p className="text-sm text-gray-600">Brand: {item.brandName}</p>
                        <div className="mt-2 space-y-1">
                          <p className="text-sm text-blue-600 font-medium">
                            PI: {item.proformaInvoiceNumber} ({new Date(item.proformaInvoiceDate).toLocaleDateString()})
                          </p>
                          <p className="text-sm text-gray-500">
                            {item.recipient?.type}: {item.recipient?.name} 
                            {item.recipient?.phone && item.recipient?.phone !== 'N/A' && (
                              <span> | Ph: {item.recipient?.phone}</span>
                            )}
                          </p>
                          {item.recipient?.station && item.recipient?.station !== 'N/A' && (
                            <p className="text-sm text-gray-500">Station: {item.recipient?.station}</p>
                          )}
                          {item.salesmen && item.salesmen !== 'N/A' && (
                            <p className="text-sm text-gray-500">Salesman: {item.salesmen}</p>
                          )}
                          <p className="text-xs text-gray-400">
                            Quote Type: {item.quotationType === 'quotation-new' ? 'New Quotation' : 'Tally Quotation'}
                          </p>
                        </div>
                      </div>
                      <div className="flex-shrink-0 ml-4">
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                          Mandatory
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {item.quantityType === 'boxes-pieces' ? (
                        <>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Boxes</label>
                            <input
                              type="number"
                              value={item.boxes || 0}
                              onChange={(e) => handleMandatoryItemChange(index, 'boxes', parseInt(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              min="0"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Pieces</label>
                            <input
                              type="number"
                              value={item.pieces || 0}
                              onChange={(e) => handleMandatoryItemChange(index, 'pieces', parseInt(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              min="0"
                            />
                          </div>
                        </>
                      ) : (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Quantity</label>
                          <input
                            type="number"
                            value={item.quantity || 0}
                            onChange={(e) => handleMandatoryItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            min="0"
                          />
                        </div>
                      )}
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Current Stock</label>
                        <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                          {item.currentStock || 0} {item.unit}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Reserved Stock</label>
                        <div className="px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                          {item.reservedStock || 0} {item.unit}
                        </div>
                      </div>
                    </div>

                    {item.stockValidationMessage && (
                      <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <p className="text-sm text-orange-800">{item.stockValidationMessage}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-between">
              <button
                onClick={() => setStep(1)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                Back
              </button>
              <button
                onClick={handleMandatoryItemsNext}
                className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Next: Additional Items
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-purple-900 mb-2">Step 3: Additional Items (Optional)</h3>
              <p className="text-purple-700 text-sm">Add any additional items that you want to include in this purchase indent.</p>
            </div>

            {/* Item Selection */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900">Add Additional Items</h4>
                <div className="flex items-center text-sm text-gray-500 space-x-4">
                  <span>Found: {availableItems.length} items</span>
                  {filteredItemsCount > 0 && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-orange-100 text-orange-800">
                      {filteredItemsCount} already selected
                    </span>
                  )}
                  {itemSearchLoading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  )}
                </div>
              </div>
              
              {/* Search Bar */}
              <div className="mb-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    value={itemSearch}
                    onChange={(e) => setItemSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Search items by name, code, category..."
                  />
                  {itemSearch && (
                    <button
                      onClick={() => setItemSearch('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>
              </div>

              {/* Items Grid */}
              {itemSearchLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600">Searching items...</span>
                </div>
              ) : availableItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <svg className="h-12 w-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-4h-2m-4 0h-2M9 9h2" />
                  </svg>
                  <p>{itemSearch ? `No items found for "${itemSearch}"` : 'No items available'}</p>
                  {itemSearch && (
                    <button
                      onClick={() => setItemSearch('')}
                      className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {availableItems.map((item, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h5 className="text-sm font-medium text-gray-900 break-words leading-relaxed">
                            {item.itemName}
                          </h5>
                          {item.itemDescription && (
                            <p className="text-xs text-gray-500 mt-1">
                              {item.itemDescription}
                            </p>
                          )}
                          <div className="mt-2 space-y-1">
                            <p className="text-xs text-gray-500">
                              Brand: {item.brand ? item.brand.name : 'N/A'}
                            </p>
                            <p className="text-xs text-gray-500">
                              Stock: {item.closingBalance || 0}
                            </p>
                            <p className="text-xs text-blue-600">
                              Type: {item.itemType === 'TallyItems' ? 'Tally Item' : 'Regular Item'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddAdditionalItem(item)}
                          className="ml-2 p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                          title="Add Item"
                        >
                          <PlusIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Added Additional Items */}
            {form.additionalItems.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Added Additional Items</h4>
                {form.additionalItems.map((item, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 break-words leading-relaxed">
                          {item.itemName}
                        </h4>
                        <p className="text-sm text-gray-600">Brand: {item.brandName}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                          Additional
                        </span>
                        <button
                          onClick={() => handleRemoveAdditionalItem(index)}
                          className="p-1 text-red-600 hover:text-red-800"
                          title="Remove Item"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {item.quantityType === 'boxes-pieces' ? (
                        <>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Boxes</label>
                            <input
                              type="number"
                              value={item.boxes || 0}
                              onChange={(e) => handleAdditionalItemChange(index, 'boxes', parseInt(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              min="0"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Pieces</label>
                            <input
                              type="number"
                              value={item.pieces || 0}
                              onChange={(e) => handleAdditionalItemChange(index, 'pieces', parseInt(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              min="0"
                            />
                          </div>
                        </>
                      ) : (
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Quantity</label>
                          <input
                            type="number"
                            value={item.quantity || 0}
                            onChange={(e) => handleAdditionalItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            min="0"
                          />
                        </div>
                      )}
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Current Stock</label>
                        <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                          {item.currentStock || 0} {item.unit}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Reserved Stock</label>
                        <div className="px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                          {item.reservedStock || 0} {item.unit}
                        </div>
                      </div>
                    </div>

                    {item.stockValidationMessage && (
                      <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <p className="text-sm text-orange-800">{item.stockValidationMessage}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-between">
              <button
                onClick={() => setStep(2)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                Back
              </button>
              <button
                onClick={() => {
                  validateStock();
                  setStep(4);
                }}
                className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Next: Review & Submit
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Step 4: Review & Submit</h3>
              <p className="text-gray-700 text-sm">Review all the details before submitting the purchase indent.</p>
            </div>

            {/* Summary */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Purchase Indent Summary</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium">Brand:</span> {form.brand?.label}
                </div>
                <div>
                  <span className="font-medium">Date Range:</span> {form.dateRange.startDate?.toLocaleString()} - {form.dateRange.endDate?.toLocaleString()}
                </div>
                <div>
                  <span className="font-medium">Total Items:</span> {form.mandatoryItems.length + form.additionalItems.length}
                </div>
              </div>
            </div>

            {/* Remarks */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
              <textarea
                rows={3}
                value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Add any additional notes or remarks..."
              />
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setStep(3)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || (form.mandatoryItems.length === 0 && form.additionalItems.length === 0)}
                className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting...' : 'Submit'} Purchase Indent
                <CheckIcon className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">Purchase Indent Management</h1>
            <p className="text-gray-600">Manage purchase indents with mandatory items from proforma invoices</p>
          </div>
          
          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setTabValue(0)}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                tabValue === 0
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Purchase Indents List
            </button>
            <button
              onClick={() => {
                setTabValue(1);
                setStep(1);
                resetForm();
              }}
              className={`px-6 py-3 text-sm font-medium transition-colors ${
                tabValue === 1
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Create New Purchase Indent
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {tabValue === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Filters */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-64">
                  <ReactSelect
                    value={brands.find(b => b.value === filters.brandId) || null}
                    options={brands}
                    onChange={(selectedOption) => setFilters({ ...filters, brandId: selectedOption?.value || '' })}
                    placeholder="Filter by brand..."
                    isClearable
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>
                <div className="flex-1 min-w-64">
                  <ReactSelect
                    value={statusOptions.find(s => s.value === filters.status) || null}
                    options={statusOptions}
                    onChange={(selectedOption) => setFilters({ ...filters, status: selectedOption?.value || '' })}
                    placeholder="Filter by status..."
                    isClearable
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Indent Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Brand
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date Range
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                        Loading...
                      </td>
                    </tr>
                  ) : purchaseIndents.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                        No purchase indents found
                      </td>
                    </tr>
                  ) : (
                    purchaseIndents.map((indent) => (
                      <tr key={indent._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {indent.indentNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {indent.brand?.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(indent.dateRange.startDate).toLocaleString()} - {new Date(indent.dateRange.endDate).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {indent.totalItems}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(indent.status)}`}>
                            {indent.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleView(indent)}
                              className="text-blue-600 hover:text-blue-900 cursor-pointer transition-colors"
                              title="View Details"
                            >
                              <EyeIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    Page {page} of {totalPages}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tabValue === 1 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Create New Purchase Indent
                </h2>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1">
                    {[1, 2, 3, 4].map((stepNum) => (
                      <div
                        key={stepNum}
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                          stepNum === step
                            ? 'bg-blue-600 text-white'
                            : stepNum < step
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {stepNum < step ? <CheckIcon className="h-4 w-4" /> : stepNum}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6">
              {renderStepContent()}
            </div>
          </div>
        )}

        {/* Stock Validation Modal */}
        {stockValidationModal.open && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Stock Validation Results
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {stockValidationModal.items.map((item, index) => (
                    <div key={index} className="p-3 border border-gray-200 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 break-words leading-relaxed">
                            {item.itemName}
                          </h4>
                          <p className="text-sm text-gray-600">Brand: {item.brandName}</p>
                        </div>
                        <div className="flex-shrink-0 ml-4">
                          {item.stockValidationMessage ? (
                            <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />
                          ) : (
                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                          )}
                        </div>
                      </div>
                      {item.stockValidationMessage && (
                        <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-sm text-orange-800">
                          {item.stockValidationMessage}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end space-x-4 mt-6">
                  <button
                    onClick={() => setStockValidationModal({ open: false, items: [] })}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Purchase Indent Modal */}
        {viewModal.open && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Purchase Indent Details - {viewModal.purchaseIndent?.indentNumber}
                  </h3>
                  <button
                    onClick={() => setViewModal({ open: false, purchaseIndent: null })}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Basic Information */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">Basic Information</h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Indent Number:</span> {viewModal.purchaseIndent?.indentNumber}</div>
                      <div><span className="font-medium">Brand:</span> {viewModal.purchaseIndent?.brand?.name}</div>
                      <div><span className="font-medium">Status:</span> 
                        <span className={`ml-2 inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(viewModal.purchaseIndent?.status)}`}>
                          {viewModal.purchaseIndent?.status}
                        </span>
                      </div>
                      <div><span className="font-medium">Date Range:</span> {new Date(viewModal.purchaseIndent?.dateRange?.startDate).toLocaleString()} - {new Date(viewModal.purchaseIndent?.dateRange?.endDate).toLocaleString()}</div>
                      <div><span className="font-medium">Created By:</span> {viewModal.purchaseIndent?.createdBy?.name}</div>
                      <div><span className="font-medium">Created At:</span> {new Date(viewModal.purchaseIndent?.createdAt).toLocaleString()}</div>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Total Items:</span> {viewModal.purchaseIndent?.totalItems || 0}</div>
                      <div><span className="font-medium">Total Quantity:</span> {viewModal.purchaseIndent?.totalQuantity || 0}</div>
                      <div><span className="font-medium">Estimated Cost:</span> {formatCurrency(viewModal.purchaseIndent?.estimatedCost || 0)}</div>
                      <div><span className="font-medium">Mandatory Items:</span> {viewModal.purchaseIndent?.mandatoryItems?.length || 0}</div>
                      <div><span className="font-medium">Additional Items:</span> {viewModal.purchaseIndent?.additionalItems?.length || 0}</div>
                    </div>
                  </div>
                </div>

                {/* Remarks */}
                {viewModal.purchaseIndent?.remarks && (
                  <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Remarks</h4>
                    <p className="text-sm text-gray-700">{viewModal.purchaseIndent.remarks}</p>
                  </div>
                )}

                {/* Mandatory Items */}
                {viewModal.purchaseIndent?.mandatoryItems && viewModal.purchaseIndent.mandatoryItems.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-medium text-gray-900 mb-3">Mandatory Items</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {viewModal.purchaseIndent.mandatoryItems.map((item, index) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div>
                                  <div className="font-medium">{item.itemName}</div>
                                  <div className="text-gray-500">{item.brandName}</div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div>
                                  <div className="font-medium">{item.recipient?.name}</div>
                                  <div className="text-gray-500">{item.recipient?.station}</div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.quantityType === 'boxes-pieces' 
                                  ? `${item.boxes} boxes, ${item.pieces} pieces`
                                  : `${item.quantity} ${item.unit || ''}`
                                }
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <div className="text-gray-900">Current: {item.currentStock}</div>
                                <div className="text-gray-500">Required: {item.requiredQuantity}</div>
                                {item.stockValidationMessage && (
                                  <div className="text-orange-600 text-xs mt-1">{item.stockValidationMessage}</div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Additional Items */}
                {viewModal.purchaseIndent?.additionalItems && viewModal.purchaseIndent.additionalItems.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-medium text-gray-900 mb-3">Additional Items</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {viewModal.purchaseIndent.additionalItems.map((item, index) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div>
                                  <div className="font-medium">{item.itemName}</div>
                                  <div className="text-gray-500">{item.brandName}</div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {item.quantityType === 'boxes-pieces' 
                                  ? `${item.boxes} boxes, ${item.pieces} pieces`
                                  : `${item.quantity} ${item.unit || ''}`
                                }
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <div className="text-gray-900">Current: {item.currentStock}</div>
                                <div className="text-gray-500">Required: {item.requiredQuantity}</div>
                                {item.stockValidationMessage && (
                                  <div className="text-orange-600 text-xs mt-1">{item.stockValidationMessage}</div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setViewModal({ open: false, purchaseIndent: null })}
                    className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseIndent;